/**
 * New Authentication System Types
 * Comprehensive type definitions for the revamped authentication system
 */

// Role type to support both string codes and full role objects
export interface UserRole {
  id?: number;
  roleCode: string;
  roleName: string;
  description?: string;
  classification?: string;
  scopeKind?: string;
  principalKind?: string;
  status?: string;
}

// Core Authentication Types
export interface User {
  id: string;
  email: string;
  name: string;
  userName?: string;
  displayName?: string;
  roles: (string | UserRole)[]; // Support both role codes and full role objects
  logins?: any[]; // Complete login data from API
  systemPermissions: UserPermission[];
  // Login tracking fields
  lastLoginAt?: string;
  previousLoginAt?: string;
  lastLoginProvider?: string;
  loginCount?: number;
  rowVersion?: string; // Base64-encoded for optimistic concurrency control
}

export interface UserPreferences {
  // Identity
  userId?: number;
  
  // Language & Localization
  language: string;  // BCP47 format: en-US, zh-CN, etc.
  
  // Theme & Appearance
  theme: 'light' | 'dark' | 'system';
  colorTheme: 'yellow' | 'green' | 'blue' | 'purple';
  
  // Date & Time
  timezone: string;  // IANA timezone: UTC, Asia/Shanghai, etc.
  dateFormat: 'relative' | 'absolute';
  dateTimeFormatPattern?: string | null;  // Absolute token pattern, e.g. 'yyyy/MM/dd HH:mm:ss' (used when dateFormat === 'absolute')
  
  // Notifications
  emailNotifications: boolean;
  pushNotifications: boolean;
  
  // Notification Preferences
  notificationSoundEnabled?: boolean;
  notificationSoundVolume?: number;
  notificationToastEnabled?: boolean;
  notificationToastDuration?: number;
  notificationToastPosition?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
  notificationSystemEnabled?: boolean;
  notificationToolEnabled?: boolean;
  notificationAdminEnabled?: boolean;
  notificationSecurityEnabled?: boolean;
  notificationDndEnabled?: boolean;
  notificationDndStartTime?: string;
  notificationDndEndTime?: string;
  
  // Active working-context triple (tenant + buyer + supplier) — active-party-top-bar
  activeTenantId?: number | null;
  activeBuyerPartyId?: number | null;
  activeSupplierPartyId?: number | null;

  // Extended Settings (for future use)
  extendedSettings?: Record<string, any>;
  
  // Audit fields (read-only)
  createdAt?: string;
  createdBy?: string;
  updatedAt?: string;
  updatedBy?: string;
}

export interface Tenant {
  id: string;
  name: string;
  domain: string;
  isDefault: boolean;
}

export interface Permission {
  resource: string;
  actions: string[];
}

// Union type to handle both string permissions and structured permissions
export type UserPermission = string | Permission;

export enum TokenStatus {
  Valid = 'valid',
  Expired = 'expired',
  Refreshing = 'refreshing',
  Invalid = 'invalid'
}

// Token Management Types
export interface TokenPair {
  accessToken: string;
  refreshToken: string;
  expiresAt: number; // Use number timestamp instead of Date
  tokenType: 'Bearer';
}

// Multi-Account Types (migrated from legacy system)
export interface StoredAccount {
  id: string;
  loginName: string;
  userName: string;
  email: string;
  profilePicture?: string;
  encryptedCredentials: string;
  lastUsed: number;
  rememberMe: boolean;
  persistenceDays: number;
}

// Authentication State (Extended with Multi-Account Support)
export interface AuthState {
  // Authentication status
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitialized: boolean;
  
  // User information
  user: User | null;
  
  // Tenant context
  tenants: Tenant[];
  currentTenant: Tenant | null;
  tenantPermissions: UserPermission[];
  
  // Multi-account management (migrated from legacy system)
  activeAccountId: string | null;
  availableAccounts: StoredAccount[];
  isAccountSwitching: boolean;
  accountSwitchError: string | null;
  
  // Account management operations
  isAddingAccount: boolean;
  addAccountError: string | null;
  isRemovingAccount: boolean;
  removeAccountError: string | null;
  
  // Account validation
  lastValidationTime: number | null;
  validationErrors: Record<string, string>;
  
  // Error handling
  error: AuthError | null;
  lastOperation: string | null;
  
  // Token status
  tokenStatus: TokenStatus;
}

// Error Handling Types
export enum AuthErrorType {
  // Network-related errors
  NetworkTimeout = 'network_timeout',
  NetworkUnavailable = 'network_unavailable',
  ServerError = 'server_error',
  
  // Authentication errors
  InvalidCredentials = 'invalid_credentials',
  TokenExpired = 'token_expired',
  TokenInvalid = 'token_invalid',
  OAuthError = 'oauth_error',
  
  // Authorization errors
  InsufficientPermissions = 'insufficient_permissions',
  TenantAccessDenied = 'tenant_access_denied',
  
  // Data errors
  UserDataUnavailable = 'user_data_unavailable',
  PreferencesLoadFailed = 'preferences_load_failed',
  TenantDataUnavailable = 'tenant_data_unavailable',
  
  // System errors
  StorageUnavailable = 'storage_unavailable',
  WorkflowInterrupted = 'workflow_interrupted',
  StateCorruption = 'state_corruption'
}

export interface AuthError {
  type: AuthErrorType;
  message: string;
  userMessage: string;
  code: string;
  recoverable: boolean;
  retryable: boolean;
  context?: Record<string, any>;
  timestamp: number; // Use number instead of Date for Redux serialization
}

// Authentication Operations
export interface LoginCredentials {
  loginName: string;
  password: string;
}

export interface AuthResult {
  success: boolean;
  error?: string;
  requiresTenantSelection?: boolean;
}

// Workflow Types
export interface WorkflowStep {
  name: string;
  execute(): Promise<StepResult>;
  rollback?(): Promise<void>;
}

export interface StepResult {
  success: boolean;
  data?: any;
  error?: string;
}

export interface WorkflowResult {
  success: boolean;
  completedSteps: string[];
  error?: WorkflowError;
}

export interface WorkflowError {
  step: string;
  message: string;
  code: string;
  recoverable: boolean;
}

// Redux Action Payloads
export interface LoginSuccessPayload {
  user: User;
  tenants: Tenant[];
  currentTenant: Tenant;
  permissions: Permission[];
}

// Multi-Account Action Payloads
export interface AccountSwitchPayload {
  secret: any; // AuthSecret from legacy system
  accountId: string;
  accounts: StoredAccount[];
}

export interface AddAccountPayload {
  accounts: StoredAccount[];
  newAccountId?: string;
}

export interface RemoveAccountPayload {
  accounts: StoredAccount[];
  removedAccountId: string;
  newActiveAccountId?: string;
  newSecret?: any; // AuthSecret from legacy system
  isCompleteLogout?: boolean;
}

export interface AccountMetadataUpdate {
  accountId: string;
  updates: Partial<StoredAccount>;
}

export interface ValidationPayload {
  validationTime: number;
  errors: Record<string, string>;
}

// Storage Models
export interface StoredAuthData {
  tokens: TokenPair;
  user: User;
  preferences: UserPreferences;
  currentTenant: Tenant;
  lastActivity: number; // Use number instead of Date
}

export interface SessionAuthData {
  workflowState: WorkflowState;
  pendingTenantSwitch: string | null;
  errorRecoveryData: ErrorRecoveryData | null;
}

export interface WorkflowState {
  currentStep: string;
  completedSteps: string[];
  startTime: number; // Use number instead of Date
}

export interface ErrorRecoveryData {
  lastError: AuthError;
  retryCount: number;
  lastRetryTime: number; // Use number instead of Date
}

// Service Response Types
export interface TokenResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  tokenType: string;
}

// Migration Types (for backward compatibility)
export interface LegacyAuthSecret {
  loginName: string;
  userName: string;
  token: {
    accessToken: string;
    accessToken_LifeInMinutes: number;
    accessToken_Expires: string;
    refreshToken: string;
    refreshToken_LifeInMinutes: number;
    refreshToken_Expires: string;
  };
}

export interface LegacyUser {
  id: number;
  guid: string | null;
  userName: string | null;
  displayName: string | null;
  givenName: string | null;
  surname: string | null;
  email: string | null;
  logins: any[];
  roles: any[];
}